import React, { MouseEventHandler, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import {
  fetchMoreArchiveSearchResults,
  restoreArchivedContact,
  restoreArchivedContactBatch,
  setSelectedArchivedContact,
  setSelectedArchivedContactBatch,
} from '@repo/redux/actions/archive';
import Header from '@components/Header';
import { ArchivedContactHeader } from '@repo/components/headers';
import Container from '@components/ContentContainer';
import { ListRowContent } from '@repo/components/lists';
import { createListContext, List as VirtualizedList } from '@components/List/VirtualizedList';
import Contact from '@repo/utils/models/Contact';
import styles from './Archive.module.scss';
import { ContactAvatar } from '@repo/components/avatars';
import { LoaderIcon, RestoreIcon } from '@repo/icons';
import { KeyboardShortcut, Key } from '@repo/components/tooltips';
import { Overlay } from '@repo/components/tooltips';
import { RESTORE_HOTKEY } from '@views/CommandBar/actionsBySection/archivedContactActions';
import { useHotkeyHandler } from '@repo/hooks';
import { ID, ViewID } from '@repo/utils/types';
import Sources from '@components/Contact/Sources';
import { useScrollRestoration } from '@components/utils/use-scroll-restoration';
import EmptyArchive from './EmptyArchive';
import { PeopleHeader } from '@views/People';
import { GroupMembershipCount } from '@views/Group/Header';
import { metaTrigger } from '@repo/utils/helpers/keyboardHandler';
import {
  selectArchivedContact,
  selectArchiveSearch,
} from '@repo/redux/reducers/data/archiveSearch';
import {
  selectHighlightedArchivedContact,
  selectSelectedArchivedContact,
  selectSelectedArchivedContactBatch,
} from '@repo/redux/reducers/ui/archiveView';

const ArchiveListContext = createListContext();

const ArchivedContactAvatar = ({ contact }: { contact: Contact }) => (
  <ListRowContent className={styles.avatar}>
    <ContactAvatar size="sm" contact={contact} />
  </ListRowContent>
);

const ArchivedContactActionIcons = ({ contact }) => {
  const dispatch = useDispatch();
  const restoreContact: MouseEventHandler = useCallback((e) => {
    e.stopPropagation();
    dispatch(restoreArchivedContact(contact.id));
  }, []);

  return (
    <ListRowContent className={styles.rowActions}>
      <KeyboardShortcut
        placement="bottom"
        overlay={
          <Overlay>
            <Key>{RESTORE_HOTKEY}</Key>Restore
          </Overlay>
        }
      >
        <RestoreIcon width={19} height={19} onClick={restoreContact} hoverOpacity />
      </KeyboardShortcut>
    </ListRowContent>
  );
};

const ArchivedContactRestoring = () => (
  <ListRowContent className={cn(styles.rowActions, styles.flipped)}>
    <LoaderIcon width={19} height={19} />
  </ListRowContent>
);

function ArchivedContactRow({ contactID, isHighlighted }) {
  const contact = useSelector(selectArchivedContact(contactID));
  let rightSideContent = <ArchivedContactAvatar contact={contact} />;

  if (isHighlighted) {
    rightSideContent = <ArchivedContactActionIcons contact={contact} />;
  }

  if (contact.isFetching) {
    rightSideContent = <ArchivedContactRestoring />;
  }

  return (
    <>
      <ListRowContent className={cn(styles.contact, isHighlighted && styles.highlighted)}>
        {contact.displayName}
      </ListRowContent>
      {rightSideContent}
    </>
  );
}

const renderArchiveItem = (contact: Contact, isHighlighted: boolean) => (
  <ArchivedContactRow contactID={contact.id} isHighlighted={isHighlighted} />
);

const ArchivedContactPreview = ({ contactID }: { contactID?: ID }) => {
  const archivedContact = useSelector(selectArchivedContact(contactID));

  return (
    !!archivedContact && (
      <div className={styles.sidebarContent}>
        <ArchivedContactHeader className={styles.previewHeader} sidebar contact={archivedContact} />
        <Sources contactID={archivedContact.id} sources={archivedContact?.information} />
      </div>
    )
  );
};

const ArchiveMembershipCount = () => {
  const archive = useSelector(selectArchiveSearch);
  const archiveCount = Math.max(archive.results.totalResults, 0);

  if (archive.term !== 'all') {
    return (
      <GroupMembershipCount count={archiveCount}>
        &nbsp;found for "{archive.term}"
      </GroupMembershipCount>
    );
  }
  return <GroupMembershipCount count={archiveCount} />;
};

const viewID: ViewID = 'Archive';
export default function Archive() {
  const dispatch = useDispatch();
  const headerRef = useRef(null);
  const scrollingContainerRef = useRef(null);
  const selectedContactBatch = useSelector(selectSelectedArchivedContactBatch);
  const selectedContact = useSelector(selectSelectedArchivedContact);
  const highlightedContact = useSelector(selectHighlightedArchivedContact);

  const archiveSearch = useSelector(selectArchiveSearch);
  const loadNextPage = useCallback(() => dispatch(fetchMoreArchiveSearchResults()), []);

  const results = useMemo(() => {
    if (archiveSearch.results.filteredItems.size === 0) {
      return [];
    }

    return [{ id: 'sectionHeader', label: <ArchiveMembershipCount /> }].concat(
      // @ts-ignore
      archiveSearch.results.filteredItems.valueSeq().toArray(),
    );
  }, [archiveSearch.results.updated]);

  const defaultHighlightedIdx = useMemo(() => {
    if (highlightedContact) {
      const highlightedIdx = results.findIndex(({ id }) => id == highlightedContact.id);
      return highlightedIdx || 0;
    }
    return 1;
  }, []);

  useEffect(() => {
    if (results.length === 0) {
      dispatch(setSelectedArchivedContact(null));
    } else {
      const highlightedIdx = results.findIndex(({ id }) => id == highlightedContact?.id);
      if (highlightedIdx === -1) {
        dispatch(setSelectedArchivedContact(null));
      }
    }

    if (selectedContactBatch.size > 0) {
      dispatch(
        setSelectedArchivedContactBatch(
          selectedContactBatch
            .filter((contactID) => results.some(({ id }) => id === contactID))
            .toSet(),
        ),
      );
    }
  }, [results]);

  useScrollRestoration({ viewID, scrollingContainerRef });

  useHotkeyHandler(
    {
      handler: (e) => {
        if (e.key === RESTORE_HOTKEY && !metaTrigger(e)) {
          e.preventDefault();
          if (selectedContactBatch.size > 0) {
            dispatch(restoreArchivedContactBatch(selectedContactBatch));
          } else if (selectedContact) {
            dispatch(restoreArchivedContact(selectedContact.id));
          }
        }
      },
    },
    [selectedContact, selectedContactBatch],
  );

  return (
    <Container>
      <Container.Primary
        className={styles.container}
        id="content"
        header={
          <Header className={styles.listHeader} actions={false}>
            <div className={styles.listHeaderInner}>
              <PeopleHeader />
              <div ref={headerRef} />
            </div>
          </Header>
        }
      >
        <VirtualizedList
          multiselect
          shouldResetHighlight={false}
          // @ts-ignore
          items={results}
          renderItem={renderArchiveItem}
          canLoadMore={archiveSearch.results.canLoadMore}
          loadMore={loadNextPage}
          selectedItems={selectedContactBatch}
          setSelectedItems={(contactIDs) => dispatch(setSelectedArchivedContactBatch(contactIDs))}
          headerRef={headerRef}
          ListContext={ArchiveListContext}
          estimatedCount={archiveSearch.results.totalResults}
          onItemHighlight={(contact: Contact) => dispatch(setSelectedArchivedContact(contact))}
          headerCutoff={-1}
          contextMenuTarget="ArchivedContact"
          ref={scrollingContainerRef}
          defaultHighlightedIdx={defaultHighlightedIdx}
          determineItemSize={(item) => (item.id == 'sectionHeader' ? 36 : 48)}
          emptyListView={<EmptyArchive />}
          hasListHeader
        />
      </Container.Primary>
      <Container.Sidebar>
        <ArchivedContactPreview contactID={highlightedContact?.id} />
      </Container.Sidebar>
    </Container>
  );
}
